import {
    Alert,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import {
    checkVerifyCode,
    register,
    requestVerifyCode,
} from "../../../application/actions/authActions";
import { getMessageSlice } from "../../../application/selectors/message";
import { clearMessage, setMessage } from "../../../application/slices/message";
import { wrapFormikHandleChange } from "../../../infrastructure/utils/formatters";
import {
  attachReferralRegistrationIfNeeded,
  getReferralFieldsForSignup,
  hasActiveReferral,
} from "../../../infrastructure/utils/referralTracking";
import {
  attachContractorReferralRegistrationIfNeeded,
  getContractorReferralFieldsForSignup,
  resolveContractorReferralCode,
  storeContractorReferralFallback,
  trackContractorReferralVisit,
} from "../../../infrastructure/utils/contractorReferralTracking";
import { resolvePostLoginUrl } from "../../../infrastructure/utils/authSession";
import { ReactComponent as UnlockIcon } from "../../assets/icons/unlock.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user.svg";
import LogoIcon from "../../assets/images/logo-icon.png";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const userKindOptions = [
  { label: "متقاضی", value: "Customer" },
  { label: "پیمانکاران", value: "Contractor" },
  { label: "تامین کنندگان", value: "Supplier" },
  { label: "سرمایه گذاران", value: "Investor" },
];

const SignupPage = () => {
  const query = useQuery();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message } = useSelector(getMessageSlice);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  // Re-bind contractor referral from URL/localStorage so /c/{code} never loses attribution.
  useEffect(() => {
    const referralCode = resolveContractorReferralCode({
      search: location.search,
    });
    if (!referralCode) return undefined;

    let cancelled = false;
    storeContractorReferralFallback(referralCode);
    trackContractorReferralVisit({
      referralCode,
      landingUrl: window.location.href,
      channel: "contractor_signup",
      force: false,
    }).catch(() => {
      if (!cancelled) storeContractorReferralFallback(referralCode);
    });

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  // دریافت userKind از path param یا query
  const defaultUserKindRaw = params.userKind || query.get("userKind");
  const defaultUserKind =
    typeof defaultUserKindRaw === "string"
      ? defaultUserKindRaw.charAt(0).toUpperCase() + defaultUserKindRaw.slice(1)
      : "Customer";

  const userKindLabel =
    userKindOptions.find(
      (u) => u.value.toLowerCase() === String(defaultUserKind).toLowerCase()
    )?.label || "";

  const headerTitle = userKindLabel
    ? `فرم ثبت‌نام ${userKindLabel}`
    : "فرم ثبت‌نام";

  const getInitialValues = () => {
    if (step === 1) {
      return {
        mobile: "",
      };
    } else if (step === 2) {
      return {
        verifyCode: "",
      };
    } else {
      return {
        nationalCode: "",
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
        rememberMe: false,
        userKind: defaultUserKind,
        mobile: mobileNumber, // اضافه کردن شماره موبایل تایید شده
      };
    }
  };

  const initialValues = getInitialValues();

  const step1ValidationSchema = Yup.object().shape({
    mobile: Yup.string()
      .matches(/^09\d{9}$/, "شماره موبایل معتبر نیست.")
      .required("شماره موبایل الزامی است."),
  });

  const step2ValidationSchema = Yup.object().shape({
    verifyCode: Yup.string()
      .matches(/^\d{5}$/, "کد تایید باید ۵ رقم باشد")
      .required("کد تایید الزامی است"),
  });

  const step3ValidationSchema = Yup.object().shape({
    nationalCode: Yup.string().required("کد ملی الزامی است."),
    firstName: Yup.string().required("نام الزامی است."),
    lastName: Yup.string().required("نام خانوادگی الزامی است."),
    password: Yup.string()
      .min(6, "رمز عبور باید حداقل ۶ کاراکتر باشد.")
      .required("رمز عبور الزامی است."),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        "تأیید رمز عبور با رمز عبور مطابقت ندارد."
      )
      .required("تأیید رمز عبور الزامی است."),
    userKind: Yup.string().required("نوع کاربر الزامی است."),
    rememberMe: Yup.bool(),
    mobile: Yup.string(), // اضافه کردن فیلد موبایل بدون اجباری کردن آن
  });

  const validationSchema =
    step === 1
      ? step1ValidationSchema
      : step === 2
      ? step2ValidationSchema
      : step3ValidationSchema;

  const handleRequestVerifyCode = async (mobile, { setSubmitting }) => {
    setLoading(true);
    try {
      await dispatch(requestVerifyCode(mobile)).unwrap();
      setMobileNumber(mobile);
      setStep(2);
    } catch (error) {
      console.error("Error requesting verification code:", error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (code, { setSubmitting } = {}) => {
    setLoading(true);
    try {
      const res = await dispatch(
        checkVerifyCode({ mobileNumber, verifyCode: code, forSignup: true })
      ).unwrap();

      const authData = res?.data ?? res?.Data ?? null;
      const accessToken = authData?.accessToken ?? authData?.AccessToken;

      // Existing complete account: OTP login token is returned.
      if (accessToken) {
        dispatch(
          setMessage({
            text:
              res?.message ||
              res?.Message ||
              "کاربری با این شماره موبایل قبلاً ثبت شده است. لطفاً وارد شوید.",
            type: "warning",
          })
        );
        navigate("/login", { replace: true });
        return;
      }

      setVerificationCode(code);
      setStep(3);
    } catch (error) {
      console.error("Error verifying code:", error);
    } finally {
      setLoading(false);
      if (typeof setSubmitting === "function") setSubmitting(false);
    }
  };

  const onSubmit = (formValue, { setSubmitting }) => {
    if (step === 1) {
      handleRequestVerifyCode(formValue.mobile, { setSubmitting });
    } else if (step === 2) {
      handleVerifyCode(formValue.verifyCode, { setSubmitting });
    } else {
      setLoading(true);
      const finalFormData = {
        ...formValue,
        mobile: mobileNumber,
        verificationCode,
        ...getReferralFieldsForSignup(),
        ...getContractorReferralFieldsForSignup(),
      };

      dispatch(register(finalFormData))
        .unwrap()
        .then(async ({ user }) => {
          try {
            await attachReferralRegistrationIfNeeded();
            await attachContractorReferralRegistrationIfNeeded();
          } catch {
            /* non-blocking */
          }
          dispatch(
            setMessage({
              text: "ثبت‌نام با موفقیت انجام شد.",
              type: "success",
            })
          );
          navigate(resolvePostLoginUrl("/", user), { replace: true });
        })
        .catch(() => {
          setLoading(false);
          setSubmitting(false);
        });
    }
  };

  return (
    <Card variant="outlined" className="auth-card-container">
      <CardContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize={true} // اضافه کردن این گزینه برای بازنشانی مقادیر اولیه
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} className="auth-header">
                  <img src={LogoIcon} alt="Logo" className="auth-logo" />
                  <div className="auth-title">{headerTitle}</div>
                </Grid>

                {hasActiveReferral() && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      ثبت‌نام شما با کد معرف ثبت می‌شود.
                    </Alert>
                  </Grid>
                )}

                {step === 1 && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="شماره همراه"
                      name="mobile"
                      value={values.mobile}
                      onChange={wrapFormikHandleChange(handleChange)}
                      onBlur={handleBlur}
                      error={touched.mobile && Boolean(errors.mobile)}
                      helperText={touched.mobile && errors.mobile}
                      inputProps={{ maxLength: 11 }}
                    />
                  </Grid>
                )}

                {step === 2 && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="کد تایید"
                      name="verifyCode"
                      value={values.verifyCode}
                      onChange={wrapFormikHandleChange(handleChange)}
                      onBlur={handleBlur}
                      error={touched.verifyCode && Boolean(errors.verifyCode)}
                      helperText={touched.verifyCode && errors.verifyCode}
                      inputProps={{ maxLength: 5 }}
                    />
                  </Grid>
                )}

                {step === 3 && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="نام"
                        name="firstName"
                        value={values.firstName}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        error={touched.firstName && Boolean(errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="نام خانوادگی"
                        name="lastName"
                        value={values.lastName}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        error={touched.lastName && Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="کد ملی"
                        name="nationalCode"
                        value={values.nationalCode}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <UserIcon />
                            </InputAdornment>
                          ),
                        }}
                        error={
                          touched.nationalCode && Boolean(errors.nationalCode)
                        }
                        helperText={touched.nationalCode && errors.nationalCode}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="password"
                        label="رمز عبور"
                        name="password"
                        value={values.password}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <UnlockIcon />
                            </InputAdornment>
                          ),
                        }}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="password"
                        label="تأیید رمز عبور"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        error={
                          touched.confirmPassword &&
                          Boolean(errors.confirmPassword)
                        }
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="rememberMe"
                            checked={values.rememberMe}
                            onChange={wrapFormikHandleChange(handleChange)}
                          />
                        }
                        label="مرا به خاطر بسپار"
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() =>
                      step === 1 ? navigate("/auth/login") : setStep(step - 1)
                    }
                  >
                    {step === 1 ? "انصراف" : "مرحله قبل"}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  {step === 2 && (
                    <Button
                      color="secondary"
                      variant="text"
                      onClick={() =>
                        handleRequestVerifyCode(mobileNumber, {
                          setSubmitting: () => {},
                        })
                      }
                      disabled={loading}
                      sx={{ mb: 1 }}
                      fullWidth
                    >
                      ارسال مجدد کد
                    </Button>
                  )}
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || loading}
                  >
                    {loading && <CircularProgress size={16} color="inherit" />}
                    {step === 1
                      ? "دریافت کد تایید"
                      : step === 2
                      ? "تایید کد"
                      : "ثبت‌نام"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>

        {message && (
          <Alert severity={message.type || "error"} sx={{ mt: 2 }}>
            {message.text || message}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default SignupPage;
